import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Slider,
  useTheme,
  Alert,
  FormHelperText,
} from '@mui/material';
import { X, Send } from 'lucide-react';
import { Form, FormField, URL_VALIDATION_REGEX } from '../../types/form';

interface FormPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  form: Form;
}

// Mirrors the agent backend's evaluateConditional semantics. Preview boolean
// values are real JS booleans, so they are normalized to the "yes"/"no"
// literals the chat widget sends at runtime.
const isFieldVisible = (
  field: FormField,
  values: Record<string, string | boolean | number>
): boolean => {
  if (!field.conditional) return true;
  let dependentValue = values[field.conditional.dependsOn];
  if (typeof dependentValue === 'boolean') {
    dependentValue = dependentValue ? 'yes' : 'no';
  }
  if (dependentValue === undefined || dependentValue === '') return false;

  const { operator, value } = field.conditional.showWhen;
  switch (operator) {
    case 'equals':
      return String(dependentValue) === String(value);
    case 'notEquals':
      return String(dependentValue) !== String(value);
    case 'contains':
      return String(dependentValue).toLowerCase().includes(String(value).toLowerCase());
    case 'in':
      return Array.isArray(value) ? value.includes(String(dependentValue)) : false;
    case 'greaterThan':
      return Number(dependentValue) > Number(value);
    case 'lessThan':
      return Number(dependentValue) < Number(value);
    default:
      return true;
  }
};

const FormPreviewDialog: React.FC<FormPreviewDialogProps> = ({ open, onClose, form }) => {
  const theme = useTheme();
  const [values, setValues] = useState<Record<string, string | boolean | number>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  // Sort fields by order
  const sortedFields = [...form.fields].sort((a, b) => a.order - b.order);

  const handleChange = (fieldId: string, value: string | boolean | number) => {
    setValues(prev => {
      const newValues = { ...prev, [fieldId]: value };
      // Forward-prune values of fields that this change hides (mirrors the
      // agent's computeVisibility, so hidden answers never linger)
      sortedFields.forEach(f => {
        if (!isFieldVisible(f, newValues)) {
          delete newValues[f.fieldId];
        }
      });
      return newValues;
    });
    // Clear error when user starts typing
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const validateField = (
    field: FormField,
    value: string | boolean | number | undefined
  ): string | null => {
    // Required check
    if (field.required && (value === undefined || value === '' || value === null)) {
      return 'This field is required';
    }

    // Skip validation for empty non-required fields
    if (!value && !field.required) return null;

    // Type-specific validation
    if (field.validation && typeof value === 'string') {
      switch (field.validation.type) {
        case 'MIN_LENGTH':
          if (value.length < Number(field.validation.value)) {
            return field.validation.errorMessage;
          }
          break;
        case 'MAX_LENGTH':
          if (value.length > Number(field.validation.value)) {
            return field.validation.errorMessage;
          }
          break;
        case 'EMAIL':
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            return field.validation.errorMessage;
          }
          break;
        case 'PHONE':
          const phoneRegex = /^[\d\s\-+()]+$/;
          if (!phoneRegex.test(value)) {
            return field.validation.errorMessage;
          }
          break;
        case 'URL':
          if (!URL_VALIDATION_REGEX.test(value)) {
            return field.validation.errorMessage;
          }
          break;
        case 'REGEX':
          try {
            const regex = new RegExp(String(field.validation.value));
            if (!regex.test(value)) {
              return field.validation.errorMessage;
            }
          } catch {
            // Invalid regex, skip validation
          }
          break;
        case 'NUMBER_RANGE':
          const num = Number(value);
          if (isNaN(num)) {
            return 'Please enter a valid number';
          }
          if (field.validation.min !== undefined && num < field.validation.min) {
            return field.validation.errorMessage;
          }
          if (field.validation.max !== undefined && num > field.validation.max) {
            return field.validation.errorMessage;
          }
          break;
      }
    }

    // Range type validation for number fields
    if (field.rangeType && value !== undefined && value !== '') {
      const num = typeof value === 'number' ? value : Number(value);
      if (isNaN(num)) {
        return 'Please enter a valid number';
      }
      if (num < field.rangeType.min) {
        return `Value must be at least ${field.rangeType.min}`;
      }
      if (num > field.rangeType.max) {
        return `Value must be at most ${field.rangeType.max}`;
      }
    }

    return null;
  };

  const handleSubmit = () => {
    // Validate all fields
    const newErrors: Record<string, string> = {};
    sortedFields.forEach(field => {
      if (
        field.fieldType === 'ai' ||
        field.fieldType === 'http_request' ||
        field.fieldType === 'file' || // preview file input is a disabled stub
        !isFieldVisible(field, values) // hidden fields are never validated
      )
        return; // Computed fields, not user-input
      const error = validateField(field, values[field.fieldId]);
      if (error) {
        newErrors[field.fieldId] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Show success message
    setSubmitted(true);
  };

  const handleReset = () => {
    setValues({});
    setErrors({});
    setSubmitted(false);
  };

  const renderField = (field: FormField) => {
    const value = values[field.fieldId];
    const error = errors[field.fieldId];

    switch (field.fieldType) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <TextField
            key={field.fieldId}
            label={field.label}
            value={value || ''}
            onChange={e => handleChange(field.fieldId, e.target.value)}
            fullWidth
            required={field.required}
            placeholder={field.placeholder}
            error={!!error}
            helperText={error}
            sx={{ mb: 2 }}
          />
        );

      case 'number':
        if (field.rangeType) {
          const { min: rangeMin, max: rangeMax } = field.rangeType;
          const sliderValue = typeof value === 'number' ? value : rangeMin;
          const range = rangeMax - rangeMin;
          const step =
            field.rangeType.step ?? (range <= 100 ? 1 : Math.max(1, Math.floor(range / 100)));
          const marks = [
            { value: rangeMin, label: String(rangeMin) },
            { value: rangeMax, label: String(rangeMax) },
          ];
          return (
            <Box key={field.fieldId} sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {field.label}
                {field.required && <span style={{ color: theme.palette.error.main }}> *</span>}
              </Typography>
              <Box sx={{ px: 1 }}>
                <Slider
                  value={sliderValue}
                  onChange={(_e, newValue) => handleChange(field.fieldId, newValue as number)}
                  min={rangeMin}
                  max={rangeMax}
                  step={step}
                  marks={marks}
                  valueLabelDisplay="on"
                />
              </Box>
              {error && (
                <FormHelperText error sx={{ ml: 1 }}>
                  {error}
                </FormHelperText>
              )}
            </Box>
          );
        }
        return (
          <TextField
            key={field.fieldId}
            label={field.label}
            value={value || ''}
            onChange={e => handleChange(field.fieldId, e.target.value)}
            fullWidth
            required={field.required}
            placeholder={field.placeholder}
            type="number"
            error={!!error}
            helperText={error}
            sx={{ mb: 2 }}
          />
        );

      case 'textarea':
        return (
          <TextField
            key={field.fieldId}
            label={field.label}
            value={value || ''}
            onChange={e => handleChange(field.fieldId, e.target.value)}
            fullWidth
            required={field.required}
            placeholder={field.placeholder}
            multiline
            rows={3}
            error={!!error}
            helperText={error}
            sx={{ mb: 2 }}
          />
        );

      case 'select':
        return (
          <FormControl
            key={field.fieldId}
            fullWidth
            required={field.required}
            error={!!error}
            sx={{ mb: 2 }}
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value || ''}
              label={field.label}
              onChange={e => handleChange(field.fieldId, e.target.value)}
            >
              {field.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        );

      case 'boolean':
        return (
          <Box key={field.fieldId} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!value}
                  onChange={e => handleChange(field.fieldId, e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    {field.label}
                    {field.required && <span style={{ color: theme.palette.error.main }}> *</span>}
                  </Typography>
                </Box>
              }
            />
            {error && (
              <FormHelperText error sx={{ ml: 2 }}>
                {error}
              </FormHelperText>
            )}
          </Box>
        );

      case 'date':
        return (
          <TextField
            key={field.fieldId}
            label={field.label}
            value={value || ''}
            onChange={e => handleChange(field.fieldId, e.target.value)}
            fullWidth
            required={field.required}
            type="date"
            InputLabelProps={{ shrink: true }}
            error={!!error}
            helperText={error}
            sx={{ mb: 2 }}
          />
        );

      case 'time':
        return (
          <TextField
            key={field.fieldId}
            label={field.label}
            value={value || ''}
            onChange={e => handleChange(field.fieldId, e.target.value)}
            fullWidth
            required={field.required}
            type="time"
            InputLabelProps={{ shrink: true }}
            error={!!error}
            helperText={error}
            sx={{ mb: 2 }}
          />
        );

      case 'file': {
        const acceptedTypes = field.fileConfig?.acceptedTypes || [];
        const maxFiles = field.fileConfig?.maxFiles || 1;
        const maxSizeMb = field.fileConfig?.maxSizeMb || 10;
        return (
          <Box key={field.fieldId} sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {field.label}
              {field.required && <span style={{ color: theme.palette.error.main }}> *</span>}
            </Typography>
            <TextField
              fullWidth
              disabled
              type="file"
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                multiple: maxFiles > 1,
                accept: acceptedTypes.map(ext => `.${ext}`).join(','),
              }}
              sx={{
                '& .MuiInputBase-root.Mui-disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                },
              }}
            />
            <FormHelperText>
              {`${acceptedTypes.map(ext => `.${ext}`).join(', ')} — ${
                maxFiles > 1 ? `up to ${maxFiles} files` : '1 file'
              }, max ${maxSizeMb}MB each (upload available in the chat widget)`}
            </FormHelperText>
          </Box>
        );
      }

      case 'ai':
        if (field.showResultToUser === false) {
          return (
            <Box key={field.fieldId} sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {field.label} (calculated silently)
              </Typography>
            </Box>
          );
        }
        return (
          <Box key={field.fieldId} sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {field.label}
            </Typography>
            <TextField
              fullWidth
              disabled
              placeholder="AI-generated result"
              size="small"
              sx={{
                '& .MuiInputBase-root.Mui-disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                },
              }}
            />
          </Box>
        );

      case 'http_request':
        return (
          <Box key={field.fieldId} sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {field.label} (executes HTTP request silently)
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box>
          <Typography variant="h6">{form.formName}</Typography>
          <Typography variant="caption" color="text.secondary">
            Preview Mode
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {submitted ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              {form.successMessage}
            </Alert>
            <Button variant="outlined" onClick={handleReset}>
              Reset Form
            </Button>
          </Box>
        ) : (
          <>
            {form.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {form.description}
              </Typography>
            )}

            {sortedFields.length === 0 ? (
              <Alert severity="info">No fields have been added to this form yet.</Alert>
            ) : (
              <>
                {sortedFields
                  .filter(field => isFieldVisible(field, values))
                  .map(field => renderField(field))}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button variant="contained" startIcon={<Send size={16} />} onClick={handleSubmit}>
                    Submit
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FormPreviewDialog;
